import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, Calendar } from "lucide-react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import ArticleCard from "@/components/ArticleCard";
import LeadDialog from "@/components/LeadDialog";
import { Button } from "@/components/ui/button";

const ArticleDetail = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/articles/${slug}`).then((r) => {
      setArticle(r.data);
      api.get("/articles", { params: { category: r.data.category, limit: 4 } })
        .then((rr) => setRelated(rr.data.filter((a) => a.slug !== slug).slice(0, 3)));
    }).catch(() => setArticle(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="mx-auto max-w-3xl px-5 py-16"><Skeleton className="h-96 w-full rounded-xl" /></div>;
  if (!article) return (
    <div className="py-32 text-center" data-testid="article-not-found">
      <p className="text-muted-foreground">Article not found.</p>
      <Link to="/articles" className="text-electric font-medium">Back to articles</Link>
    </div>
  );

  const date = new Date(article.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  return (
    <div data-testid="article-detail">
      <div className="relative bg-navy text-white">
        <div className="absolute inset-0 overflow-hidden">
          {article.cover_image && <img src={article.cover_image} alt="" className="w-full h-full object-cover opacity-25" />}
          <div className="absolute inset-0 bg-navy/70" />
        </div>
        <div className="relative mx-auto max-w-3xl px-5 lg:px-8 py-16">
          <Link to="/articles" className="inline-flex items-center gap-1 text-sm text-white/70 hover:text-white mb-6" data-testid="back-to-articles">
            <ArrowLeft className="h-4 w-4" /> All articles
          </Link>
          <Badge className="bg-electric text-white hover:bg-electric rounded-full">{article.category}</Badge>
          <h1 className="mt-4 font-display text-3xl lg:text-5xl font-extrabold leading-tight">{article.title}</h1>
          {article.subtitle && <p className="mt-4 text-lg text-white/75">{article.subtitle}</p>}
          <div className="mt-6 flex flex-wrap items-center gap-5 text-sm text-white/60">
            <span className="font-medium text-white">{article.author}</span>
            <span>{article.publication}</span>
            <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {date}</span>
            <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {article.read_minutes} min read</span>
          </div>
        </div>
      </div>

      <article className="mx-auto max-w-3xl px-5 lg:px-8 py-14">
        <div className="article-body" dangerouslySetInnerHTML={{ __html: article.body }} />

        <div className="mt-14 rounded-2xl bg-secondary p-8 border border-border" data-testid="article-lead-cta">
          <h3 className="font-display text-xl font-bold text-navy">Want the full research behind this?</h3>
          <p className="mt-2 text-muted-foreground text-sm">Access gated reports, benchmarks and white papers from Lead ITES.</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Button asChild className="btn-electric rounded-full"><Link to="/reports">Browse Reports</Link></Button>
            <LeadDialog
              source="article"
              title="Get tailored insights"
              trigger={<Button variant="outline" className="rounded-full" data-testid="article-subscribe">Request Insights</Button>}
            />
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="bg-secondary/60 py-16">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <h2 className="font-display text-2xl font-extrabold text-navy mb-8">Related coverage</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((a, i) => <ArticleCard key={a.id} article={a} index={i} />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ArticleDetail;
